import { useRef, useMemo } from 'react';
import { useCalendarState } from '@react-stately/calendar';
import { useCalendar, useCalendarGrid, useCalendarCell } from '@react-aria/calendar';
import { useLocale } from '@react-aria/i18n';
import { useTranslation } from '@hooks/useTranslation';
import { useButton } from '@react-aria/button';
import {
  createCalendar,
  getWeeksInMonth,
  today,
  isSameDay,
} from '@internationalized/date';
import type { CalendarDate, DateValue } from '@internationalized/date';
import type { CalendarState } from '@react-stately/calendar';
import type { AriaCalendarProps } from '@react-aria/calendar';
import styles from './DatePicker.module.scss';

// ============================================
// TYPES
// ============================================

export interface CalendarProps {
  /** Currently selected date (CalendarDate from @internationalized/date) */
  value?: CalendarDate | null;
  /** Called when a date is selected */
  onChange?: (value: CalendarDate) => void;
  /** Minimum selectable date */
  minValue?: CalendarDate;
  /** Maximum selectable date */
  maxValue?: CalendarDate;
  /** Function to determine if a specific date is unavailable */
  isDateUnavailable?: (date: CalendarDate) => boolean;
  /** Whether the calendar is disabled */
  isDisabled?: boolean;
  /** Whether the calendar is read-only */
  isReadOnly?: boolean;
}

// ============================================
// YEAR RANGE — show 10 years before and after
// ============================================

const YEAR_RANGE_BEFORE = 100;
const YEAR_RANGE_AFTER = 10;

// ============================================
// MONTH NAMES
// ============================================

function useMonthNames() {
  const { t } = useTranslation();
  return useMemo(() => [
    t('K_MONTH_JANUARY', 'January'),
    t('K_MONTH_FEBRUARY', 'February'),
    t('K_MONTH_MARCH', 'March'),
    t('K_MONTH_APRIL', 'April'),
    t('K_MONTH_MAY', 'May'),
    t('K_MONTH_JUNE', 'June'),
    t('K_MONTH_JULY', 'July'),
    t('K_MONTH_AUGUST', 'August'),
    t('K_MONTH_SEPTEMBER', 'September'),
    t('K_MONTH_OCTOBER', 'October'),
    t('K_MONTH_NOVEMBER', 'November'),
    t('K_MONTH_DECEMBER', 'December'),
  ], [t]);
}

// ============================================
// CALENDAR COMPONENT
// ============================================

/**
 * A shared calendar grid component used by DatePicker and DateTimePicker.
 * Built on react-aria for full accessibility and keyboard navigation.
 */
function Calendar({
  value,
  onChange,
  minValue,
  maxValue,
  isDateUnavailable,
  isDisabled = false,
  isReadOnly = false,
}: CalendarProps) {
  const { locale } = useLocale();

  const ariaProps: AriaCalendarProps<DateValue> = {
    value,
    onChange: onChange as ((value: DateValue) => void) | undefined,
    minValue,
    maxValue,
    isDateUnavailable: isDateUnavailable as
      | ((date: DateValue) => boolean)
      | undefined,
    isDisabled,
    isReadOnly,
  };

  const state = useCalendarState({
    ...ariaProps,
    locale,
    createCalendar,
  });

  const { calendarProps, prevButtonProps, nextButtonProps } =
    useCalendar(ariaProps, state);

  return (
    <div {...calendarProps} className={styles.calendar}>
      <CalendarHeader
        state={state}
        prevButtonProps={prevButtonProps}
        nextButtonProps={nextButtonProps}
      />
      <CalendarGrid state={state} />
    </div>
  );
}

// ============================================
// CALENDAR HEADER — month/year dropdowns + nav
// ============================================

interface CalendarHeaderProps {
  state: CalendarState;
  prevButtonProps: ReturnType<typeof useCalendar>['prevButtonProps'];
  nextButtonProps: ReturnType<typeof useCalendar>['nextButtonProps'];
}

function CalendarHeader({
  state,
  prevButtonProps,
  nextButtonProps,
}: CalendarHeaderProps) {
  const monthNames = useMonthNames();
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  const { buttonProps: prevBtnProps } = useButton(prevButtonProps, prevRef);
  const { buttonProps: nextBtnProps } = useButton(nextButtonProps, nextRef);

  const focusedDate = state.focusedDate;
  const currentMonth = focusedDate.month;
  const currentYear = focusedDate.year;

  const todayDate = today('UTC');
  const minYear = todayDate.year - YEAR_RANGE_BEFORE;
  const maxYear = todayDate.year + YEAR_RANGE_AFTER;

  const years: number[] = [];
  for (let y = minYear; y <= maxYear; y++) {
    years.push(y);
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = Number(e.target.value);
    state.setFocusedDate(focusedDate.set({ month: newMonth }));
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = Number(e.target.value);
    state.setFocusedDate(focusedDate.set({ year: newYear }));
  };

  return (
    <div className={styles.calendarHeader}>
      <button
        {...prevBtnProps}
        ref={prevRef}
        className={styles.navButton}
        aria-label="Previous month"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 12L6 8l4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className={styles.calendarHeaderTitle}>
        <select
          value={currentMonth}
          onChange={handleMonthChange}
          className={styles.calendarSelect}
          aria-label="Month"
        >
          {monthNames.map((name, idx) => (
            <option key={idx} value={idx + 1}>
              {name}
            </option>
          ))}
        </select>

        <select
          value={currentYear}
          onChange={handleYearChange}
          className={styles.calendarSelect}
          aria-label="Year"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      <button
        {...nextBtnProps}
        ref={nextRef}
        className={styles.navButton}
        aria-label="Next month"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}

// ============================================
// CALENDAR GRID — day cells
// ============================================

interface CalendarGridProps {
  state: CalendarState;
}

function CalendarGrid({ state }: CalendarGridProps) {
  const { locale } = useLocale();
  const { gridProps, headerProps, weekDays } = useCalendarGrid(
    { weekdayStyle: 'short' },
    state,
  );

  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);
  const weeks: number[] = [];
  for (let i = 0; i < weeksInMonth; i++) {
    weeks.push(i);
  }

  return (
    <table {...gridProps} className={styles.calendarGrid}>
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, idx) => (
            <th key={idx} className={styles.calendarHeaderCell}>
              {day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {weeks.map((weekIdx) => (
          <tr key={weekIdx}>
            {state.getDatesInWeek(weekIdx).map((date, dayIdx) =>
              date ? (
                <CalendarCell key={dayIdx} state={state} date={date} />
              ) : (
                <td key={dayIdx} />
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ============================================
// CALENDAR CELL — individual day
// ============================================

interface CalendarCellProps {
  state: CalendarState;
  date: CalendarDate;
}

function CalendarCell({ state, date }: CalendarCellProps) {
  const ref = useRef<HTMLDivElement>(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isDisabled,
    isUnavailable,
    isOutsideVisibleRange,
    formattedDate,
  } = useCalendarCell({ date }, state, ref);

  const isCurrentToday = isSameDay(date, today('UTC'));

  const cellClasses = [
    styles.calendarCell,
    isSelected && styles.calendarCellSelected,
    isCurrentToday && !isSelected && styles.calendarCellToday,
    isDisabled && styles.calendarCellDisabled,
    isUnavailable && styles.calendarCellUnavailable,
    isOutsideVisibleRange && styles.calendarCellOutside,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <td {...cellProps}>
      <div {...buttonProps} ref={ref} className={cellClasses}>
        {formattedDate}
      </div>
    </td>
  );
}

export default Calendar;
